import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react'
import TimelineRuler from './TimelineRuler'
import TimelineTrack from './TimelineTrack'
import WordLevelTimelineTrack from './WordLevelTimelineTrack'
import TimelineToolbar from './TimelineToolbar'
import useTimelineState from '../../hooks/useTimelineState'

/**
 * TimelineEditor Component
 * Main coordinator for the timeline editor
 * Manages state and coordinates child components
 */
const TimelineEditor = ({
  song,
  initialLyrics,
  wordLevelTranscription = null,
  resetKey,
  currentTime,
  onSeek,
  onLyricsChange,
  onWordLevelChange,
  onHasUnsavedChanges,
  onSave,
  onSaveWordLevel,
  isSaving = false,
  // Playback controls
  isPlaying = false,
  onPlay,
  onPause,
  onSkipBack,
  onSkipForward,
  canSkipBack = false,
  canSkipForward = false,
  audioEngineReady = true,
  audioError = false
}, ref) => {
  const [scrollLeft, setScrollLeft] = useState(0)

  // Word-level editing state
  const [editableWordLevel, setEditableWordLevel] = useState(null)
  const [selectedWordKey, setSelectedWordKey] = useState(null)
  const [wordLevelHasChanges, setWordLevelHasChanges] = useState(false)

  // Initialize editable word-level from prop
  useEffect(() => {
    if (wordLevelTranscription) {
      // Deep clone the word-level transcription
      setEditableWordLevel(JSON.parse(JSON.stringify(wordLevelTranscription)))
      setWordLevelHasChanges(false)
    } else {
      setEditableWordLevel(null)
    }
  }, [wordLevelTranscription])

  // Use timeline state hook
  const {
    lyrics,
    selectedBlockIndex,
    zoom,
    snapToGrid,
    hasUnsavedChanges,
    canUndo,
    canRedo,
    updateLyric,
    addLyric,
    removeLyric,
    setSelectedBlockIndex,
    setZoom,
    setSnapToGrid,
    undo,
    redo,
    snapTime,
    resetLyrics,
    replaceLyrics,
    validateLyrics
  } = useTimelineState(initialLyrics, resetKey)

  // Notify parent of unsaved changes (line-level or word-level)
  useEffect(() => {
    if (onHasUnsavedChanges) {
      onHasUnsavedChanges(hasUnsavedChanges || wordLevelHasChanges)
    }
  }, [hasUnsavedChanges, wordLevelHasChanges, onHasUnsavedChanges])

  // Notify parent of lyrics changes
  useEffect(() => {
    if (!onLyricsChange) return

    const clonedLyrics = Array.isArray(lyrics)
      ? lyrics.map((lyric) => ({ ...lyric }))
      : []

    onLyricsChange(clonedLyrics)
  }, [lyrics, onLyricsChange])

  // Notify parent of word-level changes
  useEffect(() => {
    if (!onWordLevelChange || !editableWordLevel) return
    onWordLevelChange(editableWordLevel)
  }, [editableWordLevel, onWordLevelChange])

  // Handle word selection
  const handleSelectWord = useCallback((wordKey) => {
    setSelectedWordKey(wordKey)
    // Deselect line-level block when selecting a word
    if (wordKey !== null) {
      setSelectedBlockIndex(null)
    }
  }, [setSelectedBlockIndex])

  // Handle word update (drag or resize)
  const handleUpdateWord = useCallback((wordKey, newTimes) => {
    if (!editableWordLevel || !wordKey) return

    const [lineIndex, wordIndex] = wordKey.split('-').map(Number)

    setEditableWordLevel(prev => {
      if (!prev || !prev[lineIndex] || !prev[lineIndex].segments || !prev[lineIndex].segments[wordIndex]) {
        return prev
      }

      const updated = JSON.parse(JSON.stringify(prev))
      updated[lineIndex].segments[wordIndex] = {
        ...updated[lineIndex].segments[wordIndex],
        ...newTimes
      }
      return updated
    })

    setWordLevelHasChanges(true)
  }, [editableWordLevel])

  // Handle word text edit (double-click)
  const handleEditWord = useCallback((wordKey) => {
    // For now, just select the word. Could open a modal for text editing in the future.
    setSelectedWordKey(wordKey)
  }, [])

  // Handle save word-level to backend
  const handleSaveWordLevel = useCallback(() => {
    if (onSaveWordLevel && editableWordLevel) {
      onSaveWordLevel(editableWordLevel)
      setWordLevelHasChanges(false)
    }
  }, [editableWordLevel, onSaveWordLevel])

  useImperativeHandle(ref, () => ({
    openLyricEditor: (index) => {
      if (typeof index !== 'number') return
      if (!Array.isArray(lyrics) || index < 0 || index >= lyrics.length) return
      setSelectedBlockIndex(index)
    },
    replaceLyrics
  }), [lyrics, setSelectedBlockIndex, replaceLyrics])

  // Calculate pixels per second based on zoom
  const BASE_PIXELS_PER_SECOND = 100
  const timeToPixels = useCallback((timeInSeconds, zoomLevel) => {
    return timeInSeconds * BASE_PIXELS_PER_SECOND * (zoomLevel / 100)
  }, [])

  const pixelsToTime = useCallback((pixels, zoomLevel) => {
    return pixels / (BASE_PIXELS_PER_SECOND * (zoomLevel / 100))
  }, [])

  // Find current lyric index - with defensive check
  const currentLyricIndex = Array.isArray(lyrics) && lyrics.length > 0
    ? lyrics.findIndex((lyric, index) => {
        if (currentTime >= lyric.start_time) {
          if (index === lyrics.length - 1 || currentTime < lyrics[index + 1].start_time) {
            return true
          }
        }
        return false
      })
    : -1

  // Handle block selection
  const handleSelectBlock = useCallback((index) => {
    setSelectedBlockIndex(index)
    // Deselect word when selecting a line-level block
    if (index !== null) {
      setSelectedWordKey(null)
    }
  }, [setSelectedBlockIndex])

  // Handle block edit (double-click)
  const handleEditBlock = useCallback((index) => {
    if (index == null || index < 0 || !Array.isArray(lyrics)) return
    setSelectedBlockIndex(index)
  }, [lyrics, setSelectedBlockIndex])

  // Handle block update (drag or resize)
  const handleUpdateBlock = useCallback((index, newTimes) => {
    updateLyric(index, newTimes)
  }, [updateLyric])

  // Handle save lyric from modal
  const handleSaveLyric = useCallback((index, updatedLyric) => {
    updateLyric(index, updatedLyric)
  }, [updateLyric])

  // Handle add new lyric
  const handleAddLyric = useCallback(() => {
    // Add a new lyric at current time or at the end
    const safeLyrics = Array.isArray(lyrics) ? lyrics : []
    const newStartTime = currentTime >= 0 ? currentTime : (safeLyrics.length > 0 ? safeLyrics[safeLyrics.length - 1].end_time + 1 : 0)
    const newEndTime = newStartTime + 3 // 3 second duration by default

    addLyric({
      text: 'New lyric',
      translation: '',
      romanization: '',
      start_time: newStartTime,
      end_time: newEndTime
    })
  }, [currentTime, lyrics, addLyric])

  // Handle delete selected
  const handleDeleteSelected = useCallback(() => {
    if (selectedBlockIndex !== null) {
      removeLyric(selectedBlockIndex)
    }
  }, [selectedBlockIndex, removeLyric])

  // Handle save to backend (saves both line-level and word-level)
  const handleSave = useCallback(() => {
    const errors = validateLyrics()
    if (errors.length > 0) {
      // Show validation errors
      alert('Please fix validation errors:\n' + errors.map(e => `Block ${e.index + 1}: ${e.message}`).join('\n'))
      return
    }

    // Save line-level lyrics
    if (onSave && hasUnsavedChanges) {
      onSave(lyrics)
    }

    // Save word-level transcription
    if (onSaveWordLevel && wordLevelHasChanges && editableWordLevel) {
      onSaveWordLevel(editableWordLevel)
      setWordLevelHasChanges(false)
    }
  }, [lyrics, validateLyrics, onSave, hasUnsavedChanges, onSaveWordLevel, wordLevelHasChanges, editableWordLevel])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input or contentEditable region
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return
      }

      // Space: toggle play/pause
      if (e.code === 'Space' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        // Parent component handles play/pause through audio controls
      }

      // Delete/Backspace: remove selected block
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBlockIndex !== null) {
        e.preventDefault()
        handleDeleteSelected()
      }

      // Enter: edit selected block
      if (e.key === 'Enter' && selectedBlockIndex !== null) {
        e.preventDefault()
        handleEditBlock(selectedBlockIndex)
      }

      // Ctrl/Cmd + Z: undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        if (canUndo) {
          undo()
        }
      }

      // Ctrl/Cmd + Shift + Z: redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault()
        if (canRedo) {
          redo()
        }
      }

      // Ctrl/Cmd + S: save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (hasUnsavedChanges || wordLevelHasChanges) {
          handleSave()
        }
      }

      // Escape: deselect both line-level and word-level
      if (e.key === 'Escape') {
        e.preventDefault()
        setSelectedBlockIndex(null)
        setSelectedWordKey(null)
      }

      // Arrow keys: move selected block
      if (selectedBlockIndex !== null && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && Array.isArray(lyrics) && lyrics[selectedBlockIndex]) {
        e.preventDefault()
        const lyric = lyrics[selectedBlockIndex]
        const moveAmount = 0.1 // 100ms
        const direction = e.key === 'ArrowLeft' ? -1 : 1
        const duration = lyric.end_time - lyric.start_time

        const newStartTime = Math.max(0, lyric.start_time + (moveAmount * direction))
        const newEndTime = newStartTime + duration

        updateLyric(selectedBlockIndex, {
          start_time: newStartTime,
          end_time: newEndTime
        })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    selectedBlockIndex,
    lyrics,
    canUndo,
    canRedo,
    undo,
    redo,
    updateLyric,
    handleEditBlock,
    setSelectedBlockIndex,
    handleDeleteSelected,
    hasUnsavedChanges,
    wordLevelHasChanges,
    handleSave
  ])

  if (!song) {
    return (
      <div className="bg-black/30 backdrop-blur-sm rounded-lg p-8 text-center">
        <p className="text-white/60">No song selected</p>
      </div>
    )
  }

  return (
    <>
      <div className="bg-black/30 backdrop-blur-sm rounded-lg overflow-hidden w-full max-w-full min-w-0">
        {/* Timeline Toolbar */}
        <TimelineToolbar
          zoom={zoom}
          onZoomIn={setZoom}
          onZoomOut={setZoom}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleSave}
          onReset={resetLyrics}
          selectedBlockIndex={selectedBlockIndex}
          onAddLyric={handleAddLyric}
          onDeleteSelected={handleDeleteSelected}
          snapToGrid={snapToGrid}
          onToggleSnap={() => setSnapToGrid(!snapToGrid)}
          isSaving={isSaving}
          // Playback controls
          isPlaying={isPlaying}
          onPlay={onPlay}
          onPause={onPause}
          onSkipBack={onSkipBack}
          onSkipForward={onSkipForward}
          currentTime={currentTime}
          duration={song?.duration || 0}
          canSkipBack={canSkipBack}
          canSkipForward={canSkipForward}
          audioEngineReady={audioEngineReady}
          audioError={audioError}
        />

        {/* Timeline Ruler */}
        <TimelineRuler
          duration={song.duration || 0}
          currentTime={currentTime}
          zoom={zoom}
          onSeek={onSeek}
          timeToPixels={timeToPixels}
          scrollLeft={scrollLeft}
        />

        {/* Timeline Track - Line Level */}
        <div className="relative">
          <div className="absolute left-0 top-0 z-10 bg-black/60 text-white/60 text-xs px-2 py-1 rounded-br-lg">
            Line-Level (Editable)
          </div>
          <TimelineTrack
            lyrics={lyrics}
            duration={song.duration || 0}
            currentTime={currentTime}
            currentLyricIndex={currentLyricIndex}
            selectedBlockIndex={selectedBlockIndex}
            zoom={zoom}
            snapToGrid={snapToGrid}
            timeToPixels={timeToPixels}
            pixelsToTime={pixelsToTime}
            snapTime={snapTime}
            onSelectBlock={handleSelectBlock}
            onEditBlock={handleEditBlock}
            onUpdateBlock={handleUpdateBlock}
            onScrollChange={setScrollLeft}
          />
        </div>

        {/* Timeline Track - Word Level */}
        <WordLevelTimelineTrack
          wordLevelTranscription={editableWordLevel}
          duration={song.duration || 0}
          currentTime={currentTime}
          zoom={zoom}
          snapToGrid={snapToGrid}
          timeToPixels={timeToPixels}
          pixelsToTime={pixelsToTime}
          scrollLeft={scrollLeft}
          onScrollChange={setScrollLeft}
          selectedWordKey={selectedWordKey}
          onSelectWord={handleSelectWord}
          onUpdateWord={handleUpdateWord}
          onEditWord={handleEditWord}
          snapTime={snapTime}
        />

        {/* Status bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/40 border-t border-white/10 text-xs text-white/60">
          <div className="flex items-center gap-4">
            <span>Zoom: {zoom}%</span>
            <span>Lines: {Array.isArray(lyrics) ? lyrics.length : 0}</span>
            <span>Words: {editableWordLevel ? editableWordLevel.reduce((acc, line) => acc + (line.segments?.length || 0), 0) : 0}</span>
            {selectedBlockIndex !== null && (
              <span className="text-blue-400">Line: {selectedBlockIndex + 1}</span>
            )}
            {selectedWordKey !== null && (
              <span className="text-purple-400">Word: {selectedWordKey}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {(hasUnsavedChanges || wordLevelHasChanges) && (
              <span className="text-orange-400">
                Unsaved: {hasUnsavedChanges && 'Lines'}{hasUnsavedChanges && wordLevelHasChanges && ' + '}{wordLevelHasChanges && 'Words'}
              </span>
            )}
            {canUndo && <span>Undo available</span>}
            {canRedo && <span>Redo available</span>}
          </div>
        </div>
      </div>
    </>
  )
}

export default forwardRef(TimelineEditor)
